import { FC, useContext, useMemo, useRef } from "react";
import { ReportCustomizationContext } from "../ReportCustomizationContext";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user";
import { getProviderFilterOptions, handleToastError, handleToastSuccess, reportTypeToColumnsEndpointMap } from "../helpers";
import { ExportProviderAvailabilityType, HSDFormatType, NetworkProviderComparisonType, NetworkProviderFilterType, ProviderDropdownTypes } from "@app/exports/enumerations";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import ExportService from "@app/exports/exportService";
import { ExportAdequacyServicingProvidersModel } from "@app/exports/exportModels";
import { NextClosestProviderAction, PatientVolumeLabelEnum, ProviderNetworkType } from "@app/projects/enumerators";
import { GenerateSection } from "../components/GenerateSection";
import { ColumnConfigurationSection } from "../components/ColumnConfigurationSection";
import { ServiceAreaSection } from "../components/ServiceAreaSection";
import { SpecialtySection } from "../components/SpecialtySection";
import { ReportCustomizationReducerActions } from "../interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "../interfaces/reportTypeEnum";

export const ServicingProvidersReport: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const user = useContext(UserContext);
  const exportService = useRef(new ExportService()).current;

  const providerSourceOptions = useMemo(() => {
    const options = [<option key="network" value={ProviderDropdownTypes.Network}>Network Servicing Providers</option>];

    if (rcContext.routeState.hasProspectProviders) {
      options.push(<option key="prospect" disabled={rcContext.state.isCompareMode || rcContext.state.selectedMemberships.length > 1} value={ProviderDropdownTypes.Prospect}>Prospect Servicing Providers</option>);
    }

    if (rcContext.routeState.hasMarketProviders) {
      options.push(<option key="market" disabled={rcContext.state.isCompareMode || rcContext.state.selectedMemberships.length > 1} value={ProviderDropdownTypes.Market}>Market Servicing Providers</option>);
    }

    if (rcContext.routeState.hasNextClosestProviders) {
      options.push(<option key="nextclosestproviders" disabled={rcContext.state.isCompareMode || rcContext.state.selectedMemberships.length > 1} value={ProviderDropdownTypes.NextClosestProviders}>Next Closest Providers</option>)
    }

    if (rcContext.state.showNetNewProviders) {
      options.push(<option key="netnewproviders" disabled={rcContext.state.isCompareMode || rcContext.state.selectedMemberships.length > 1} value={ProviderDropdownTypes.NetNew}>Net New Servicing Providers</option>)
    }
    return options;
  }, [rcContext.routeState.hasProspectProviders, rcContext.routeState.hasProspectProviders, rcContext.routeState.hasNextClosestProviders, rcContext.state.isCompareMode, rcContext.state.selectedMemberships, rcContext.state.showNetNewProviders]);

  const providerFilterOptions = useMemo(() => {
    const filters = getProviderFilterOptions(rcContext.state.providerSource === ProviderNetworkType.Network);
    return filters.map((f) => <option key={f.key} value={f.key}>{f.value}</option>);
  }, [rcContext.state.providerSource]);

  const getExportModel = () => {
    const newComparisonTypes = [];
    if (rcContext.state.showAddedProviders)
      newComparisonTypes.push(NetworkProviderComparisonType.Added);
    if (rcContext.state.showMatchedProviders)
      newComparisonTypes.push(NetworkProviderComparisonType.Matched);
    if (rcContext.state.showRemovedProviders)
      newComparisonTypes.push(NetworkProviderComparisonType.Removed);

    const servicingProviderModel: ExportAdequacyServicingProvidersModel = {
      appendPatternOfCareProviders: rcContext.state.appendPatternOfCareProviders,
      comparedProjectRunConfigurationId: parseInt(rcContext.state.comparedTimeslice),
      customProjectId: parseInt(rcContext.routeParams.projectId),
      eMailOnSuccess: rcContext.state.shouldEmailLink,
      exportName: rcContext.state.filename,
      formatType: rcContext.state.fileFormat,
      hsdFormat: rcContext.state.reportFormat,
      impactProviderType: rcContext.state.providerSource === ProviderNetworkType.Prospect || rcContext.state.providerSource === ProviderNetworkType.Market ? rcContext.state.providerFilter as ExportProviderAvailabilityType : null,
      includeActiveRegionSets: rcContext.state.includeActive,
      includeApprovedRegionSets: rcContext.state.includeApproved,
      includeCargoColumns: rcContext.state.includeCargo,
      includeFacilitySpecialties: rcContext.state.includeFacilitySpecialties,
      includePendingRegionSets: rcContext.state.includePending,
      includePhysicianSpecialties: rcContext.state.includePhysicianSpecialties,
      isZipAnalysis: false,
      networkAssessmentAnalysisTypes: rcContext.state.networkAssessmentAnalysisTypes,
      networkProviderFilterType: rcContext.state.providerSource === ProviderNetworkType.Network ? rcContext.state.providerFilter as NetworkProviderFilterType : null,
      nextClosestProviderAction: rcContext.state.providerSource === ProviderDropdownTypes.NextClosestProviders
        ? NextClosestProviderAction.FilterToNextClosestProvidersOnly
        : rcContext.state.appendNextClosestProviders
          ? NextClosestProviderAction.AppendNextClosestProviders
          : NextClosestProviderAction.None,
      projectRunConfigurationId: parseInt(rcContext.state.timeslice),
      projectURL: `${window.customUIAbsolutePath}/projects/${rcContext.routeParams.projectId}`,
      providerClaimsVolumeFilter: PatientVolumeLabelEnum.Unknown,
      providerComparisonTypes: newComparisonTypes,
      providerIds: rcContext.routeState.providerIds,
      providerNetworkType: rcContext.state.providerSource === ProviderDropdownTypes.NextClosestProviders ? ProviderNetworkType.Network : rcContext.state.providerSource as ProviderNetworkType,
      regionSetGapFilter: rcContext.state.serviceAreaGapsType,
      regionSetIds: rcContext.state.selectedCounties.map((sa) => parseInt(sa)),
      reportCustomizationId: rcContext.state.selectedPreset?.id,
      runAsynchronous: true,
      sharedUsers: rcContext.state.sharedUsers,
      specialtyGapFilter: rcContext.state.specialtyGapsType,
      specialtyGroupCodes: rcContext.state.selectedSpecialties.map((sp) => sp.key),
    };

    return servicingProviderModel;
  };

  const isPocDisabled = rcContext.state.providerSource !== ProviderNetworkType.Network || (rcContext.state.reportFormat !== HSDFormatType.MA && rcContext.state.reportFormat !== HSDFormatType.MMP);

  // event handlers
  const handleGenerateClick = () => {
    rcContext.dispatch({ payload: { isGenerating: true }, type: ReportCustomizationReducerActions.ChangeIsGenerating });

    exportService.postExportAdequacyServicingProviders(getExportModel())
      .then(() => handleToastSuccess(rcContext.state.filename),
        () => handleToastError(rcContext.state.filename)
      ).then(() => {
        rcContext.dispatch({ payload: { isGenerating: false }, type: ReportCustomizationReducerActions.ChangeIsGenerating });
      });
  }

  return (
    <>
      <div className="container ml-0">
        <ServiceAreaSection />

        <SpecialtySection />

        <div className="row mt-3">
          <h2 className="header-s col-auto">Provider</h2>
        </div>
        <div className="row">
          <QESSelect
            label="Provider Source"
            containerClassName="col-4"
            value={rcContext.state.providerSource}
            onChange={(e) => rcContext.changeProviderSource(e.target.value as ProviderNetworkType)}
            testId="report-customization__provider-source"
          >
            {providerSourceOptions}
          </QESSelect>
        </div>

        {
          rcContext.state.providerSource === ProviderDropdownTypes.Network && (
            <QESCheckbox
              label="Append Next Closest Network Providers"
              containerClassName="row ml-1"
              checked={rcContext.state.appendNextClosestProviders}
              onChange={(e) => rcContext.dispatch({ payload: { appendNextClosestProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeAppendNextClosestProviders })}
              testId="report-customization__append-ncp"
            />
          )
        }

        {
          user.roles.includes(Roles.PATTERN_OF_CARE_USER)
          && rcContext.routeState.showPatternOfCare
          && (
            <div className="row ml-1 my-2">
              {/* <div className="col-auto"> */}
              <TooltipWrapper
                containerClassName="report-customization__append-poc"
                text={isPocDisabled
                  ? "Select Network Providers in the Provider Filter and MA HSD or MMP HSD Format to Append Pattern of Care Providers" : ""
                }
              >
                <QESCheckbox
                  label="Append Pattern of Care Providers"
                  containerClassName="my-0"
                  checked={rcContext.state.appendPatternOfCareProviders}
                  onChange={(e) => rcContext.dispatch({ payload: { appendPatternOfCareProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeAppendPatternOfCare })}
                  testId="report-customization__append-poc"
                  disabled={isPocDisabled}
                />
              </TooltipWrapper>
              {/* </div> */}
            </div>
          )
        }

        {
          (rcContext.state.providerSource === ProviderDropdownTypes.Network
            || rcContext.state.providerSource === ProviderDropdownTypes.Prospect
            || rcContext.state.providerSource === ProviderDropdownTypes.Market)
          && (
            <div className="row mt-2">
              <QESSelect
                label="Provider Filter"
                containerClassName="col-4"
                value={rcContext.state.providerFilter}
                onChange={(e) => rcContext.dispatch({ payload: { providerFilter: e.target.value as NetworkProviderFilterType }, type: ReportCustomizationReducerActions.ChangeProviderFilter })}
                testId="report-customization__provider-filter"
              >
                {providerFilterOptions}
              </QESSelect>
            </div>
          )
        }
        <div className="row mb-2">

          {((!!rcContext.state.timeslice && !!rcContext.state.comparedTimeslice) || (rcContext.state.selectedMemberships.length === 2)) && rcContext.state.providerSource === ProviderDropdownTypes.Network && (
            <div className="col-auto">
              <QESCheckbox
                label="Added Providers"
                containerClassName="ml-1"
                checked={rcContext.state.showAddedProviders}
                onChange={(e) => rcContext.dispatch({ payload: { showAddedProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShowAddedProviders })}
                testId="report-customization__added-providers"
              />
              <QESCheckbox
                label="Matched Providers"
                containerClassName="ml-1"
                checked={rcContext.state.showMatchedProviders}
                onChange={(e) => rcContext.dispatch({ payload: { showMatchedProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShowMatchedProviders })}
                testId="report-customization__matched-providers"
              />
              <QESCheckbox
                label="Removed Providers"
                containerClassName="ml-1"
                checked={rcContext.state.showRemovedProviders}
                onChange={(e) => rcContext.dispatch({ payload: { showRemovedProviders: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShowRemovedProviders })}
                testId="report-customization__removed-providers"
              />
            </div>
          )}
        </div>

        <ColumnConfigurationSection columnsEndpoint={reportTypeToColumnsEndpointMap.get(ReportType.AdequacyServicingProvider)} exportModel={getExportModel()} />

        <QESCheckbox
          containerClassName="row mt-3 "
          label="Include cargo column information"
          checked={rcContext.state.includeCargo}
          onChange={(e) => rcContext.dispatch({ payload: { includeCargo: e.target.checked }, type: ReportCustomizationReducerActions.ChangeIncludeCargo })}
          testId="report-customization__include-cargo"
        />
      </div>
      <GenerateSection
        settingsAreValid={rcContext.validateSettings()}
        onGenerate={handleGenerateClick}
      />
    </>
  );
}